import { Data } from "../typescript/interfaces/context";
import { IndicationApprovalStatus } from "../typescript/interfaces/data";
import {
  ApprovalStatusSortOrder,
  DisplayedIndicationStageSortOrder,
  IndicationApprovalStageType,
} from "../typescript/enums/data";
import { date } from ".";
import { orderBy, sortBy } from "lodash";
import { types } from "../typescript";

export const caseInsensitive = (unsortedArray: string[]): string[] =>
  unsortedArray.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

export const approvalStatus = (approvals: IndicationApprovalStatus[]): IndicationApprovalStatus[] => {
  const orderedByDate = orderBy(approvals, (o) => date.parseString(o.date), ["asc"]);

  const sortedByApprovals = sortBy(orderedByDate, [
    (ias: IndicationApprovalStatus) => {
      const approvalStage = ias.stage || "";
      return ApprovalStatusSortOrder[approvalStage];
    },
  ]);

  return sortedByApprovals;
};

export const columnData = (nextRow: Data, thisRow: Data, key: string, isDeep?: boolean, subKey?: string): number => {
  let nextRowName: string;
  let thisRowName: string;

  if (isDeep) {
    nextRowName = nextRow[key][0] ? nextRow[key][0][subKey].toLowerCase() : "";
    thisRowName = thisRow[key][0] ? thisRow[key][0][subKey].toLowerCase() : "";
  } else {
    nextRowName = nextRow[key].toLowerCase();
    thisRowName = thisRow[key].toLowerCase();
  }

  if (!nextRowName) nextRowName = "";
  if (!thisRowName) thisRowName = "";

  if (nextRowName < thisRowName) {
    return -1;
  } else if (nextRowName > thisRowName) {
    return 1;
  } else {
    return 0;
  }
};

export const rowsByColumn = (rows: Data[], column: string, order: types.table.SortOrder) => {
  switch (column) {
    case "brandName":
      return rowsByKeyOfStr(rows, column, order);
    case "brandCompanies":
    case "routesOfAdministration":
    case "mechanismsOfAction":
      return rowsByKeyOfObj(rows, column, "name", order);
    case "indications":
      return rowsByKeyOfObj(rows, "indicationApprovalStatus", "indication", order);
    case "stages":
      return rowsByStages(rows, order);
    case "submissionTypes":
      return rowsByKeyOfArr(rows, column, order);
    default:
      return rowsByKeyOfStr(rows, "", "asc");
  }
};

const rowsByKeyOfStr = (rows: Data[], key: string, order: types.table.SortOrder) => {
  return orderBy(rows, [(row) => row[key].toLowerCase()], order);
};

const rowsByKeyOfArr = (rows: Data[], key: string, order: types.table.SortOrder) => {
  return orderBy(rows, [(row) => (row[key][0] ? row[key][0].toLowerCase() : "")], order);
};

const rowsByKeyOfObj = (rows: Data[], key: string, subKey: string, order: types.table.SortOrder) => {
  return orderBy(rows, [(row) => (row[key][0] ? row[key][0][subKey].toLowerCase() : "")], order);
};

const firstStageValue = (row: Data) => {
  for (const indication of row.indicationApprovalStatus) {
    const stageValue = indication.stage || "";
    const dateValue = indication.date;
    if (Object.keys(DisplayedIndicationStageSortOrder).includes(stageValue)) {
      return [stageValue, dateValue];
    }
  }
  return ["", ""];
};

export const rowsByStages = (rows: Data[], order: types.table.SortOrder = "asc") => {
  const sortedRows = orderBy(
    rows,
    [
      (row) => {
        const firstStage = firstStageValue(row)[0];
        return DisplayedIndicationStageSortOrder[firstStage];
      },
      (row) => {
        const firstStage = firstStageValue(row)[1];
        return date.parseString(firstStage);
      },
      (row) => row.brandName,
    ],
    order
  );

  return sortedRows;
};
